import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { getPageStatus } from './utils';

import {
  DateInputWithConfirm,
  InputWithConfirm,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm
} from 'shared-components';

import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { getMomentDate, isValidDate } from '../../../../utilities/validation';
import { ConfigField } from '../../../../types';
import { passportStatusLabel } from './utils';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'frontend/student/src/store';

const useData = () => {
  const config = useSelector((state: ApplicationState) => state.config);
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);
  const Page7 = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.Page7);
  const isPIPage7Complete = useSelector((state: ApplicationState) => state.student.offer.isPIPage7Complete);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const {
    PassportStatus: passportStatusConfig,
    PassportNumber: passportNumberConfig,
    PassportExpectedDate: passportExpectedDateConfig,
    PassportExpiryDate: passportExpiryDateConfig
  }: {
    PassportStatus: ConfigField;
    PassportNumber: ConfigField;
    PassportExpectedDate: ConfigField;
    PassportExpiryDate: ConfigField;
  } = Page7;

  const {
    passportStatus = passportStatusConfig?.defaultValue,
    passportNumber = passportNumberConfig?.defaultValue,
    passportExpectedDate = passportExpectedDateConfig?.defaultValue,
    passportExpiryDate = passportExpiryDateConfig?.defaultValue
  } = personalInfo;

  const { complete, visible } = getPageStatus(config, {
    passportStatus,
    passportNumber,
    passportExpectedDate,
    passportExpiryDate
  });

  const {
    passportStatus: passportStatusVisible,
    passportNumber: passportNumberVisible,
    passportExpectedDate: passportExpectedDateVisible,
    passportExpiryDate: passportExpiryDateVisible
  } = visible;

  return {
    sectionTitle: config.Checklist.PersonalInfo.shortTitle,
    complete,
    currentStep: 7,
    steps: 10,
    passportNumber,
    passportNumberVisible,
    passportNumberConfig,
    passportStatus,
    passportStatusVisible,
    passportStatusConfig,
    passportExpectedDate,
    passportExpectedDateVisible,
    passportExpectedDateConfig,
    passportExpiryDate,
    passportExpiryDateVisible,
    passportExpiryDateConfig,
    config,
    isPageComplete: isPIPage7Complete,
    importData
  };
};

const getIsPassportImportDataPresent = data => {
  return !!data.importData.passportNumber || !!data.importData.passportExpiry;
};

const getImportApprovedFields = data => {
  const importApproved = [];
  if (getIsPassportImportDataPresent(data) && data.passportStatus === 'Yes') {
    importApproved.push(Q.PASSPORT_STATUS);
  }
  if (data.isPageComplete) {
    if (data.passportStatus === 'Yes' && data.importData.passportNumber === data.passportNumber) {
      importApproved.push(Q.PASSPORT_NUMBER);
    }
    if (data.passportStatus === 'Yes' && data.importData.passportExpiry === data.passportExpiryDate) {
      importApproved.push(Q.PASSPORT_EXPIRY);
    }
  }
  return importApproved;
};

const getImportUpdateFields = data => {
  const importUpdateEnabled = [];
  if (data.isPageComplete) {
    if (getIsPassportImportDataPresent(data) && data.passportStatus !== 'Yes') {
      importUpdateEnabled.push(Q.PASSPORT_STATUS);
    }
    if (data.passportNumber !== data.importData.passportNumber && data.passportStatus === 'Yes') {
      importUpdateEnabled.push(Q.PASSPORT_NUMBER);
    }
    if (data.passportExpiryDate !== data.importData.passportExpiry && data.passportStatus === 'Yes') {
      importUpdateEnabled.push(Q.PASSPORT_EXPIRY);
    }
  }
  return importUpdateEnabled;
};

const isValidFutureDate = date => {
  const { YYYY, DD, MM } = date;
  if (YYYY && DD && MM) {
    if (!isValidDate(date)) {
      return { isValid: false, message: 'Please ensure the date is correct' };
    } else {
      const momentDate = getMomentDate(date);
      if (!momentDate.isSameOrAfter(moment(), 'day')) {
        return {
          isValid: false,
          message: 'Passport expected date must be in the future'
        };
      } else {
        return { isValid: true };
      }
    }
  } else {
    return { isValid: false, message: '' };
  }
};

const PersonalQPage7 = () => {
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.savePersonalInfo(Pages.PAGE7, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = useCallback(
    (fieldName: string, value, refValue?) => dispatch(PIActions.createUpdatePIFieldAction(fieldName, value, refValue)),
    [dispatch]
  );
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const data = useData();
  const isPassportImportDataPresent = getIsPassportImportDataPresent(data);

  const [expectedDate, setExpectedDate] = useState(getDateFromString(data.passportExpectedDate));
  const [expectedDateErrorMsg, setExpectedDateErrorMsg] = useState('');
  const [expiryDate, setExpiryDate] = useState(getDateFromString(data.passportExpiryDate));
  const [expiryDateErrorMsg, setExpiryDateErrorMsg] = useState('');
  const [importApproved, setImportApproved] = useState(getImportApprovedFields(data));
  const [importEdited, setImportEdited] = useState([]);

  const importUpdateEnabled = getImportUpdateFields(data);
  useEffect(() => {
    if (isPassportImportDataPresent && !data.passportStatus) {
      onChange(Q.PASSPORT_STATUS, 'Yes');
      setImportApproved(prev => [...prev, Q.PASSPORT_STATUS]);
    }
  }, [data.passportStatus, isPassportImportDataPresent, onChange]);

  useEffect(() => {
    if (isPassportImportDataPresent && !data.passportStatus) {
      onChange(Q.PASSPORT_STATUS, 'Yes');
      setImportApproved(prev => [...prev, Q.PASSPORT_STATUS]);
    }
  }, [data.passportStatus, isPassportImportDataPresent, onChange]);

  const resetFields = useCallback(() => {
    onChange(Q.PASSPORT_NUMBER, '');
    onChange(Q.PASSPORT_EXPIRY, '');
    onChange(Q.PASSPORT_EXPECTED, '');

    setExpiryDate({ DD: null, MM: null, YYYY: null });
    setExpectedDate({ DD: null, MM: null, YYYY: null });
    setExpiryDateErrorMsg('');
    setExpectedDateErrorMsg('');
    setImportApproved(prev => prev.filter(q => q !== Q.PASSPORT_NUMBER));
    setImportEdited(prev => prev.filter(q => q !== Q.PASSPORT_NUMBER));
  }, [onChange]);

  const handlePassportClick = useCallback(
    question => {
      let value;
      if (question.char === '1') {
        value = 'Yes';
      } else if (question.char === '2') {
        value = 'Applied';
      } else {
        value = 'GoingToApply';
      }
      onChange(Q.PASSPORT_STATUS, value);
      resetFields();
    },
    [onChange, resetFields]
  );

  const handleExpiryDateChange = useCallback(() => {
    const { YYYY, DD, MM } = expiryDate;
    if (YYYY && DD && MM) {
      if (isValidDate(expiryDate)) {
        onChange(Q.PASSPORT_EXPIRY, getStringFromDate(expiryDate));
        if (expiryDateErrorMsg !== '') {
          setExpiryDateErrorMsg('');
        }
      } else {
        onChange(Q.PASSPORT_EXPIRY, '');
        if (expiryDateErrorMsg !== 'Please ensure the date is correct') {
          setExpiryDateErrorMsg('Please ensure the date is correct');
        }
      }
    }
  }, [expiryDate, onChange, expiryDateErrorMsg]);

  useEffect(() => {
    handleExpiryDateChange();
  }, [handleExpiryDateChange]);

  const handleExpectedDateChange = useCallback(() => {
    const { isValid, message = '' } = isValidFutureDate(expectedDate);
    if (isValid) {
      onChange(Q.PASSPORT_EXPECTED, getStringFromDate(expectedDate));
      if (expectedDateErrorMsg !== '') {
        setExpectedDateErrorMsg('');
      }
    } else {
      onChange(Q.PASSPORT_EXPECTED, '');
      if (expectedDateErrorMsg !== message && message !== '') {
        setExpectedDateErrorMsg(message);
      }
    }
  }, [expectedDate, expectedDateErrorMsg, onChange]);

  useEffect(() => {
    handleExpectedDateChange();
  }, [handleExpectedDateChange]);

  const isImportEnabled = useCallback(
    target => {
      switch (target) {
        case Q.PASSPORT_STATUS:
          return data.passportStatusVisible && isPassportImportDataPresent;
        case Q.PASSPORT_NUMBER:
          return data.passportNumberVisible && !!data.importData.passportNumber;
        case Q.PASSPORT_EXPIRY:
          return data.passportExpiryDateVisible && !!data.importData.passportExpiry;
        default:
          return false;
      }
    },
    [
      data.passportStatusVisible,
      data.passportNumberVisible,
      data.passportExpiryDateVisible,
      isPassportImportDataPresent,
      data.importData
    ]
  );

  const isImportUpdateEnabled = useCallback(
    target => {
      if (!data.isPageComplete) return false;
      switch (target) {
        case Q.PASSPORT_STATUS:
          return !!data.passportStatus && importUpdateEnabled.includes(target);
        case Q.PASSPORT_NUMBER:
          return !!data.passportNumber && importUpdateEnabled.includes(target);
        case Q.PASSPORT_EXPIRY:
          return !!data.passportExpiryDate && importUpdateEnabled.includes(target);
        default:
          return false;
      }
    },
    [data.isPageComplete, data.passportStatus, data.passportNumber, data.passportExpiryDate, importUpdateEnabled]
  );

  const isImportComplete = useCallback(
    target => {
      if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
      return importApproved.includes(target) || importEdited.includes(target);
    },
    [isImportEnabled, isImportUpdateEnabled, importApproved, importEdited]
  );

  const getComplete = useCallback(() => {
    const importQuestions = [Q.PASSPORT_STATUS, Q.PASSPORT_NUMBER, Q.PASSPORT_EXPIRY];
    const importComplete = importQuestions.every(q => isImportComplete(q));

    return data.complete && importComplete;
  }, [data.complete, isImportComplete]);

  const handleImportApprove = useCallback(
    target => {
      setImportApproved(prev => [...prev, target]);
      setImportEdited(prev => prev.filter(q => q !== target));
    },
    [setImportApproved, setImportEdited]
  );

  const handleImportEdit = useCallback(
    target => {
      setImportApproved(prev => prev.filter(q => q !== target));
      setImportEdited(prev => [...prev, target]);
    },
    [setImportApproved, setImportEdited]
  );

  const handleImportUndo = useCallback(
    target => {
      setImportApproved(prev => prev.filter(q => q !== target));
      setImportEdited(prev => prev.filter(q => q !== target));
    },
    [setImportApproved, setImportEdited]
  );

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={data.currentStep}
      steps={data.steps}
      onNext={() => onNext(data.config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={data.sectionTitle}
    >
      <Helmet>
        <title>
          {`${data.sectionTitle}: Step ${data.currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {data.passportStatusVisible && (
        <RadioOptionsWithConfirm
          key={Q.PASSPORT_STATUS}
          id={Q.PASSPORT_STATUS}
          value={passportStatusLabel(data.passportStatus)}
          label={data.passportStatusConfig?.label}
          inputHelperText={data.passportStatusConfig?.helpText}
          onClick={handlePassportClick}
          questions={[
            {
              char: '1',
              label: passportStatusLabel('Yes'),
              selected: data.passportStatus === 'Yes'
            },
            {
              char: '2',
              label: passportStatusLabel('Applied'),
              selected: data.passportStatus === 'Applied'
            },
            {
              char: '3',
              label: passportStatusLabel('GoingToApply'),
              selected: data.passportStatus === 'GoingToApply'
            }
          ]}
          importEnabled={isImportEnabled(Q.PASSPORT_STATUS)}
          importValue={'Yes'}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_STATUS)}
          importApproved={importApproved.includes(Q.PASSPORT_STATUS)}
          importEdited={importEdited.includes(Q.PASSPORT_STATUS)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, 'Yes');
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, isImportUpdateEnabled(Q.PASSPORT_STATUS) ? data.passportStatus : 'Yes');
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, isImportUpdateEnabled(Q.PASSPORT_STATUS) ? data.passportStatus : '');
          }}
        />
      )}

      {data.passportNumberVisible && (
        <InputWithConfirm
          key={Q.PASSPORT_NUMBER}
          id={Q.PASSPORT_NUMBER}
          value={data.passportNumber}
          label={data.passportNumberConfig?.label}
          inputHelperText={data.passportNumberConfig?.helpText}
          onChange={evt => onChange(Q.PASSPORT_NUMBER, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.PASSPORT_NUMBER)}
          importValue={data.importData.passportNumber}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_NUMBER)}
          importApproved={importApproved.includes(Q.PASSPORT_NUMBER)}
          importEdited={importEdited.includes(Q.PASSPORT_NUMBER)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_NUMBER);
            onChange(Q.PASSPORT_NUMBER, data.importData.passportNumber);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_NUMBER);
            onChange(
              Q.PASSPORT_NUMBER,
              isImportUpdateEnabled(Q.PASSPORT_NUMBER) ? data.passportNumber : data.importData.passportNumber
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_NUMBER);
            onChange(Q.PASSPORT_NUMBER, isImportUpdateEnabled(Q.PASSPORT_NUMBER) ? data.passportNumber : '');
          }}
        />
      )}

      {data.passportExpiryDateVisible && (
        <DateInputWithConfirm
          key={Q.PASSPORT_EXPIRY}
          id={Q.PASSPORT_EXPIRY}
          label={data.passportExpiryDateConfig?.label}
          value={expiryDate}
          inputHelperText={data.passportExpiryDateConfig?.helpText}
          errorText={expiryDateErrorMsg}
          onChangeDD={evt => {
            setExpiryDate(prev => ({
              ...prev,
              DD: evt.target.value
            }));
          }}
          onChangeMM={evt => {
            setExpiryDate(prev => ({
              ...prev,
              MM: evt.target.value
            }));
          }}
          onChangeYY={evt => {
            if (evt.target.value.length === 4) {
              setExpiryDate(prev => ({
                ...prev,
                YYYY: evt.target.value
              }));
            } else {
              setExpiryDate(prev => ({
                ...prev,
                YYYY: evt.target.value
              }));
            }
          }}
          onBlur={handleExpiryDateChange}
          importEnabled={isImportEnabled(Q.PASSPORT_EXPIRY)}
          importValue={data.importData.passportExpiry}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_EXPIRY)}
          importApproved={importApproved.includes(Q.PASSPORT_EXPIRY)}
          importEdited={importEdited.includes(Q.PASSPORT_EXPIRY)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_EXPIRY);
            setExpiryDate(getDateFromString(data.importData.passportExpiry));
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_EXPIRY);
            if (isImportUpdateEnabled(Q.PASSPORT_EXPIRY)) {
              setExpiryDate(expiryDate);
            } else {
              setExpiryDate(getDateFromString(data.importData.passportExpiry));
            }
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_EXPIRY);
            if (isImportUpdateEnabled(Q.PASSPORT_EXPIRY)) {
              setExpiryDate(getDateFromString(data.passportExpiryDate));
            } else {
              setExpiryDate({
                DD: null,
                MM: null,
                YYYY: null
              });
            }
          }}
        />
      )}

      {data.passportExpectedDateVisible && (
        <DateInputWithConfirm
          key={Q.PASSPORT_EXPECTED}
          id={Q.PASSPORT_EXPECTED}
          label={data.passportExpectedDateConfig?.label}
          value={expectedDate}
          inputHelperText={data.passportExpectedDateConfig?.helpText}
          errorText={expectedDateErrorMsg}
          onChangeDD={evt => {
            setExpectedDate(prev => ({
              ...prev,
              DD: evt.target.value
            }));
          }}
          onChangeMM={evt => {
            setExpectedDate(prev => ({
              ...prev,
              MM: evt.target.value
            }));
          }}
          onChangeYY={evt => {
            if (evt.target.value.length === 4) {
              setExpectedDate(prev => ({
                ...prev,
                YYYY: evt.target.value
              }));
            } else {
              setExpectedDate(prev => ({
                ...prev,
                YYYY: evt.target.value
              }));
            }
          }}
          onBlur={handleExpectedDateChange}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage7;
